import { Common } from 'src/domains/common/Common';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { DepositErrorMessagesState } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/errorMessages/DepositErrorMessages.state';
import { DateTime } from 'src_common/utils/time/time';
import { AccountBasicDataType } from 'src/domains/players/state/BasicDataModel';

const parseApiDateTime = (value: string): DateTime | null => {
    return DateTime.fromUTC(value) ?? DateTime.from(value);
};

export const isDepositRestrictionRecordActive = (
    depositRestriction: NonNullable<AccountBasicDataType['depositRestriction']>
): boolean => {
    if (depositRestriction.permanent === true) {
        return true;
    }

    const expiryDate = depositRestriction.expiryDate;
    if (expiryDate === null || expiryDate === undefined || expiryDate === '') {
        return false;
    }

    const parsedExpiry = parseApiDateTime(expiryDate);
    if (parsedExpiry === null) {
        return false;
    }

    return DateTime.current().isBefore(parsedExpiry);
};

export const hasActivePlayBreak = (common: Common): boolean => {
    const playBreakExpiry = BasicDataModel.get(common).basicData.valueReady?.playBreakExpiry;

    if (playBreakExpiry === null || playBreakExpiry === undefined || playBreakExpiry === '') {
        return false;
    }

    const parsedExpiry = parseApiDateTime(playBreakExpiry);
    if (parsedExpiry === null) {
        return false;
    }

    return DateTime.current().isBefore(parsedExpiry);
};

export const hasActiveDepositRestriction = (common: Common): boolean => {
    const depositRestriction = BasicDataModel.get(common).basicData.valueReady?.depositRestriction ?? null;

    if (depositRestriction === null) {
        return false;
    }

    return isDepositRestrictionRecordActive(depositRestriction);
};

const pushActiveDepositRestrictionError = (common: Common, errorMessagesState: DepositErrorMessagesState): void => {
    const depositRestriction = BasicDataModel.get(common).basicData.valueReady?.depositRestriction;
    if (depositRestriction === null || depositRestriction === undefined) {
        return;
    }

    if (depositRestriction.source === 'kycThreshold') {
        errorMessagesState.pushErrorMessage('kyc-deposit-restriction');
        return;
    }

    if (depositRestriction.source === 'backoffice') {
        errorMessagesState.pushErrorMessage('backoffice-deposit-restriction');
        return;
    }
    if (depositRestriction.source === 'automation') {
        errorMessagesState.pushErrorMessage('automation-deposit-restriction');
        return;
    }

    if (depositRestriction.expiryDate !== null && depositRestriction.expiryDate !== undefined) {
        errorMessagesState.pushErrorMessage('failed-deposit-restriction', depositRestriction.expiryDate);
        return;
    }

    errorMessagesState.pushErrorMessage('backoffice-deposit-restriction');
};

export const syncAccountDepositBlockingErrors = (
    common: Common,
    errorMessagesState: DepositErrorMessagesState,
    hasInputValue: boolean
): boolean => {
    errorMessagesState.clearErrorMessage();

    if (hasInputValue === false) {
        return false;
    }

    if (hasActivePlayBreak(common)) {
        const playBreakExpiry = BasicDataModel.get(common).basicData.valueReady?.playBreakExpiry;
        errorMessagesState.pushErrorMessage('playbreak-deposit-restriction', playBreakExpiry ?? undefined);
        return true;
    }

    if (hasActiveDepositRestriction(common)) {
        pushActiveDepositRestrictionError(common, errorMessagesState);
        return true;
    }

    return false;
};

export const isAccountDepositBlocked = (common: Common, hasInputValue: boolean): boolean => {
    if (hasInputValue === false) {
        return false;
    }

    return hasActivePlayBreak(common) || hasActiveDepositRestriction(common);
};

/** Fresh account basic data once when opening the deposit flow (e.g. after playbreak was set elsewhere). */
export const refreshAccountBasicDataForDeposit = (common: Common): Promise<void> => {
    return BasicDataModel.get(common).basicData.refreshAndWait();
};
